import React, { useEffect, useRef, useState } from 'react';

import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { useTranslation } from 'react-i18next';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import { Box, CircularProgress } from '@mui/material';
import AutoComplete from '@src/new-components/AutoComplete';
import { AUDIO_FILE_FORMAT, SPEED } from '@src/constants/voice';
import {
  DEFAULT_CLIENT_PAUSE,
  NEW_VOICE_PRESET_NAME_LENGTH,
  VALID_SPEED,
  VOICE_PRESET_NAME_LENGTH,
} from '@src/constants/tts';
import CommonInput from '@src/new-components/Input/CommonInput';
import Typography from '@src/new-components/Typography';
import ButtonLabel from '@src/new-components/Button/ButtonLabel';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import BadgeAvatar from '@src/components/BadgeAvatar';
import BackgroundMusic from '@src/containers/BackgroundMusic';
import Select from '@src/new-components/Select';
import useForm from '@src/hooks/useForm';
import { createValidationSchema } from '@src/utils/form';
import { VOICE_NAME_REGEX } from '@src/constants/voiceCloning';
import apis from '@src/apis';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { ERROR_CODE } from '@src/errors/code';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import { checkValidNumber } from '@src/services/checkValid';
import Punctuation from './Punctuation';
import { StyledMenuItem, StyledVoicePresetDialog } from './index.style';

const VoicePresetDialog = ({
  open,
  onClose,
  onCreateSuccess,
  ttsPresetDialogData,
  initValues,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { getFeatureValue } = useFeatureFlags();
  const [openVoices, setOpenVoices] = useState(false);
  const [openBackgroundMusic, setOpenBackgroundMusic] = useState(false);
  const isApply = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const handleCloseBackgroundMusic = () => setOpenBackgroundMusic(false);

  const handleCloseVoices = () => setOpenVoices(false);

  // The wider name rule only applies once vbee-tts-api accepts it: this UI
  // can go live before the backend does.
  const newNameLengthEnabled = getFeatureValue(
    FEATURE_KEYS.NEW_VOICE_PRESET_NAME_LENGTH,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const nameLength = newNameLengthEnabled
    ? NEW_VOICE_PRESET_NAME_LENGTH
    : VOICE_PRESET_NAME_LENGTH;
  const nameDescription = t('voicePresetNameDescription', nameLength);

  const validationSchema = createValidationSchema()
    .required(
      'voicePresetName',
      t('fieldIsRequired', { fieldName: t('voicePresetName') }),
    )
    .minLength('voicePresetName', nameLength.min, nameDescription)
    .pattern('voicePresetName', VOICE_NAME_REGEX, nameDescription)
    .maxLength('voicePresetName', nameLength.max, nameDescription)
    .required('voice', t('fieldIsRequired', { fieldName: t('voice') }))
    .required(
      'audioFormat',
      t('fieldIsRequired', { fieldName: t('audioFormat') }),
    )
    .required(
      'voiceSpeed',
      t('fieldIsRequired', { fieldName: t('voiceSpeed') }),
    )
    .toObject();

  const onSubmit = async (values, { setFieldError, resetForm }) => {
    try {
      setIsLoading(true);
      const params = {
        name: values.voicePresetName,
        audioType: values.audioFormat,
        speed: values.voiceSpeed,
        voiceCode: values.voice?.code,
        backgroundMusic: {
          link: values.backgroundMusicLink,
          name: values.backgroundMusicName,
          volume: values.backgroundMusicVolume,
        },
        clientPause: {
          paragraphBreak: values.clientPause.paragraphBreak,
          sentenceBreak: values.clientPause.sentenceBreak,
          majorBreak: values.clientPause.majorBreak,
          mediumBreak: values.clientPause.mediumBreak,
        },
      };
      const res = ttsPresetDialogData
        ? await apis.ttsPreset.updateTTSPreset(ttsPresetDialogData.id, params)
        : await apis.ttsPreset.createTTSPreset(params);
      setIsLoading(false);
      if (res?.errorCode === ERROR_CODE.VOICE_PRESET_EXISTED) {
        setFieldError('voicePresetName', t('voicePresetExisted'));
        return;
      }
      if (res?.errorCode === ERROR_CODE.BACKGROUND_MUSIC_INVALID) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'backgroundMusicInvalid',
          }),
        );
        return;
      }
      if (res?.status) {
        onClose();
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: t('saveProjectSuccessful'),
          }),
        );
        if (onCreateSuccess)
          onCreateSuccess({
            ...params,
            backgroundMusic: {
              link: values?.backgroundMusicLink,
              name: values?.backgroundMusicName,
              volume: values?.backgroundMusicVolume,
            },
            voice: values.voice,
            id: res?.result?.ttsPreset?.id,
            isApply: isApply.current,
          });
        resetForm();
      } else {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('errorOccurred'),
          }),
        );
      }
    } catch (error) {
      setIsLoading(false);
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorOccurred'),
        }),
      );
    }
  };

  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
    resetForm,
    setValues,
    handleBlur,
  } = useForm({
    initialValues: {
      voicePresetName: '',
      voice: null,
      audioFormat: AUDIO_FILE_FORMAT[0],
      voiceSpeed: 1,
      backgroundMusicName: '',
      backgroundMusicLink: '',
      backgroundMusicVolume: 80,
      clientPause: DEFAULT_CLIENT_PAUSE,
    },
    options: {
      resetOnSubmit: false,
    },
    onSubmit,
    validationSchema,
  });

  useEffect(() => {
    if (ttsPresetDialogData && open) {
      setValues({
        voicePresetName: ttsPresetDialogData?.name,
        voice: ttsPresetDialogData?.voice,
        audioFormat: ttsPresetDialogData?.audioType,
        voiceSpeed: ttsPresetDialogData?.speed,
        backgroundMusicName: ttsPresetDialogData?.backgroundMusic?.name,
        backgroundMusicLink: ttsPresetDialogData?.backgroundMusic?.link,
        backgroundMusicVolume:
          ttsPresetDialogData?.backgroundMusic?.volume || 80,
        clientPause: ttsPresetDialogData?.clientPause,
      });
    } else if (initValues && open) {
      setValues({
        voicePresetName: initValues?.name || '',
        voice: initValues?.voice,
        audioFormat: initValues?.audioType,
        voiceSpeed: initValues?.speed,
        backgroundMusicName: initValues?.backgroundMusic?.name,
        backgroundMusicLink: initValues?.backgroundMusic?.link,
        backgroundMusicVolume: initValues?.backgroundMusic?.volume || 80,
        clientPause: initValues?.clientPause || DEFAULT_CLIENT_PAUSE,
      });
    }
  }, [ttsPresetDialogData, open]);

  const handleChangeVoice = (newVoice) => setFieldValue('voice', newVoice);

  const handleChangeBackgroundMusic = (name, link, volume) => {
    setFieldValue('backgroundMusicName', name);
    setFieldValue('backgroundMusicLink', link);
    setFieldValue('backgroundMusicVolume', volume);
  };
  const checkInValidSpeed = (value) =>
    value < VALID_SPEED.min || value > VALID_SPEED.max;

  const handleChangeVoiceSpeed = (value) => {
    const isNumber = checkValidNumber(value);
    if (!isNumber) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'speedIsNotNumber',
        }),
      );
      return;
    }
    if (checkInValidSpeed(value)) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'speedInvalidRange',
          value: t('range', {
            min: `${VALID_SPEED.min}x`,
            max: `${VALID_SPEED.max}x`,
          }),
        }),
      );
      return;
    }
    setFieldValue('voiceSpeed', value);
  };

  const onSubmitVoicePreset = () => {
    handleSubmit();
  };

  const onSaveVoicePreset = () => {
    isApply.current = false;
    onSubmitVoicePreset();
  };

  const onSaveAndApplyVoicePreset = () => {
    isApply.current = true;
    onSubmitVoicePreset();
  };

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    resetForm();
    onClose();
  };

  const renderLabel = (label) => (
    <Typography variant="body-14-medium" color={COLOR.darkGray}>
      {label}
    </Typography>
  );

  const renderPlaceholder = (placeholder) => (
    <Typography variant="body-14-regular" color={COLOR.label2[24]}>
      {placeholder}
    </Typography>
  );

  const renderVoice = () => (
    <Box display="flex" gap="8px" alignItems="center">
      <BadgeAvatar
        width={25}
        smallImgWidth={10}
        img={values.voice?.roundImage}
        smallImg={values.voice?.language?.roundImage}
        type="image"
      />
      <Typography variant="body-14-regular" color={COLOR.darkGray}>
        {values.voice?.name}
      </Typography>
    </Box>
  );

  const renderBackgroundMusic = () => (
    <Typography variant="body-14-regular" color={COLOR.darkGray}>
      {values.backgroundMusicName}
    </Typography>
  );
  const getTextValueSpeed = (value) => {
    if (value <= 0) return t('voiceSpeed');
    if (isFocus) return value;
    return `${value}x`;
  };
  const renderHelperText = (error) => (
    <Typography variant="body-12-regular" color={COLOR.functionalRed.default}>
      {error}
    </Typography>
  );
  const renderContent = () => (
    <StyledVoicePresetDialog>
      <Box
        display="flex"
        flexDirection="row"
        gap="16px"
        alignItems="flex-start"
      >
        <CommonInput
          label={renderLabel(t('voicePresetName'))}
          placeholder={t('enterVoicePresetName')}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '44px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: COLOR.label2[48],
            },
          }}
          name="voicePresetName"
          value={values.voicePresetName}
          error={errors.voicePresetName}
          helperText={renderHelperText(errors.voicePresetName)}
          onChange={(e) => setFieldValue('voicePresetName', e.target.value)}
          onBlur={() => handleBlur('voicePresetName')}
        />
        <Select
          options={AUDIO_FILE_FORMAT.map((format) => ({
            label: format,
            value: format,
          }))}
          label={renderLabel(t('audioFormat'))}
          customStyle={{
            height: '44px',
            borderRadius: '8px',
            border: `1px solid ${COLOR.label[16]}`,
            width: '100%',
          }}
          value={values.audioFormat}
          error={errors.audioFormat}
          helperText={renderHelperText(errors.audioFormat)}
          onChange={(e) => setFieldValue('audioFormat', e.target.value)}
          onBlur={() => handleBlur('audioFormat')}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        gap="16px"
        alignItems="flex-start"
      >
        <ButtonLabel
          customStyle={{
            height: '44px',
            borderRadius: '8px',
            border: `1px solid ${
              errors.voice
                ? COLOR.functionalRed.default
                : COLOR.borderButtonCorlor
            }`,
            width: '100%',
            justifyContent: 'space-between',
            padding: '0 16px',
            alignItems: 'center',
            gap: '8px',
            '&:focus': {
              backgroundColor: 'transparent',
            },
            '&:hover': {
              border: `1px solid ${
                errors.voice
                  ? COLOR.functionalRed.default
                  : COLOR.borderButtonCorlor
              }`,
            },
          }}
          onClick={() => setOpenVoices(true)}
          variant="outlined"
          label={renderLabel(t('voice'))}
          error={errors.voice}
          helperText={renderHelperText(errors.voice)}
        >
          {values.voice?.code
            ? renderVoice()
            : renderPlaceholder(t('selectVoice'))}
          <ArrowRightRoundedIcon
            sx={{ color: COLOR.indigo[80], fontSize: '24px' }}
          />
        </ButtonLabel>
        <AutoComplete
          options={SPEED}
          label={renderLabel(t('voiceSpeed'))}
          value={getTextValueSpeed(values.voiceSpeed)}
          onChange={handleChangeVoiceSpeed}
          error={errors.voiceSpeed}
          onFocus={() => setIsFocus(true)}
          helperText={renderHelperText(errors.voiceSpeed)}
          name="voiceSpeed"
          isChangeValueWhenBlur
          onBlur={() => {
            handleBlur('voiceSpeed');
            setIsFocus(false);
          }}
          renderOption={(_, option, handleSelectedValue) => (
            <StyledMenuItem
              key={option.title}
              active={String(values.voiceSpeed) === String(option.value)}
              onClick={() => handleSelectedValue(option.value)}
              data-id="select-voice-speed"
              data-speed={option.value}
            >
              <Typography variant="body-14-medium" color={COLOR.darkGray}>
                {option.value}x
              </Typography>
              <Typography variant="body-14-medium" color={COLOR.darkGray}>
                {t(option.title)}
              </Typography>
            </StyledMenuItem>
          )}
          customStyle={{
            height: '44px',
            borderRadius: '8px',
          }}
        />
      </Box>
      <ButtonLabel
        name="backgroundMusic"
        customStyle={{
          height: '44px',
          borderRadius: '8px',
          border: `1px solid ${
            errors.backgroundMusicName
              ? COLOR.functionalRed.default
              : COLOR.borderButtonCorlor
          }`,
          width: '100%',
          justifyContent: 'space-between',
          padding: '0 16px',
          alignItems: 'center',
          gap: '8px',
          '&:focus': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            border: `1px solid ${
              errors.backgroundMusicName
                ? COLOR.functionalRed.default
                : COLOR.borderButtonCorlor
            }`,
          },
        }}
        variant="outlined"
        label={renderLabel(t('backgroundMusic'))}
        onClick={() => setOpenBackgroundMusic(true)}
        error={errors.backgroundMusicName}
        helperText={renderHelperText(errors.backgroundMusicName)}
      >
        {values.backgroundMusicName
          ? renderBackgroundMusic()
          : renderPlaceholder(t('chooseBackgroundMusic'))}
        <ArrowRightRoundedIcon
          sx={{ color: COLOR.indigo[80], fontSize: '24px' }}
        />
      </ButtonLabel>
      <Punctuation
        clientPause={values.clientPause}
        setClientPause={(clientPause) =>
          setFieldValue('clientPause', clientPause)
        }
        ttsPreset={ttsPresetDialogData}
      />
    </StyledVoicePresetDialog>
  );

  const renderAction = () => (
    <Box display="flex" gap={1}>
      <CommonButton
        customStyle={{
          height: '40px',
          borderRadius: '8px',
          border: `1px solid ${COLOR.secondary[16]}`,
          '&:focus': {
            backgroundColor: 'transparent',
          },
        }}
        variant="outlined"
        textProps={{
          color: COLOR.indigo.default,
          variantText: 'body-14-semibold',
        }}
        onClick={onSaveVoicePreset}
        disabled={isLoading}
        startIcon={
          isLoading && !isApply.current ? (
            <CircularProgress size={20} sx={{ color: COLOR.darkGray }} />
          ) : null
        }
      >
        {t('saveName')}
      </CommonButton>
      <CommonButton
        customStyle={{
          height: '40px',
          borderRadius: '8px',
        }}
        variant="contained"
        textProps={{
          color: COLOR.indigo.default,
          variantText: 'body-14-semibold',
        }}
        onClick={onSaveAndApplyVoicePreset}
        disabled={isLoading}
        startIcon={
          isLoading && isApply.current ? (
            <CircularProgress size={20} sx={{ color: COLOR.darkGray }} />
          ) : null
        }
      >
        {t('saveAndApply')}
      </CommonButton>
    </Box>
  );

  return (
    <>
      <CommonDialog
        width="640px"
        fullWidth
        open={open}
        onClose={handleClose}
        title={t('voicePreset')}
        contentComponent={renderContent()}
        actionComponent={renderAction()}
      />
      {openVoices && (
        <VoiceSelectionDialog
          open={openVoices}
          activeVoiceId={values.voice?.id}
          activeVoiceCode={values.voice?.code}
          onClose={handleCloseVoices}
          onChangeVoice={handleChangeVoice}
          hasLanguageFilter
          isApplyPreset
        />
      )}
      <BackgroundMusic
        open={openBackgroundMusic}
        currentBackgroundMusic={{
          name: values.backgroundMusicName,
          link: values.backgroundMusicLink,
          volume: values.backgroundMusicVolume,
        }}
        name="backgroundMusic"
        onClose={handleCloseBackgroundMusic}
        onChange={handleChangeBackgroundMusic}
        showUiChangeVolume
        volume={values.backgroundMusicVolume || 0}
        max={100}
      />
    </>
  );
};

export default VoicePresetDialog;
